import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, AlertTriangle, Users, Award, FileText, Layout, BadgeCheck, ThumbsUp, MailCheck, ChevronUp, ChevronDown } from 'lucide-react';
import Sidebar from '../components/layout/Sidebar';
import {
  dashboardService,
  type EngagementScope,
  type EngagementStats,
  type EngagementSchoolRow,
} from '../services/dashboardService';

function pct(part: number, whole: number): string {
  if (whole <= 0) return '0%';
  return `${Math.round((part / whole) * 100)}%`;
}

interface MetricCardProps {
  label: string;
  value: string | number;
  hint?: string;
  icon: React.ComponentType<{ className?: string }>;
  tone?: 'default' | 'good';
}

function MetricCard({ label, value, hint, icon: Icon, tone = 'default' }: MetricCardProps) {
  const toneClasses = tone === 'good' ? 'bg-green-50 text-green-600' : 'bg-blue-50 text-primary';
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 flex items-start gap-4 shadow-sm">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${toneClasses}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-medium text-gray-500">{label}</p>
        <p className="text-2xl font-bold text-gray-900 mt-1 truncate">{value}</p>
        {hint ? <p className="text-[12px] text-gray-400 mt-1">{hint}</p> : null}
      </div>
    </div>
  );
}

type SortKey = 'schoolName' | 'totalStudents' | 'activeStudents' | 'totalAchievements' | 'verifiedAchievements' | 'hypesReceived';

export default function EngagementPage() {
  const [scope, setScope] = useState<EngagementScope>('affiliated');
  const [selectedSchoolId, setSelectedSchoolId] = useState<string>(''); // '' = aggregate

  const [stats, setStats] = useState<EngagementStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);

  const [bySchool, setBySchool] = useState<EngagementSchoolRow[]>([]);
  const [bySchoolError, setBySchoolError] = useState<string | null>(null);

  const [sortKey, setSortKey] = useState<SortKey>('totalStudents');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Per-school rows: table + the school picker. Fetched once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await dashboardService.getEngagementBySchool();
        if (!cancelled) setBySchool(rows);
      } catch (err) {
        if (!cancelled) setBySchoolError(err instanceof Error ? err.message : 'Failed to load per-school stats');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    setStatsError(null);
    try {
      const data = await dashboardService.getEngagement(scope, selectedSchoolId || undefined);
      setStats(data);
    } catch (err) {
      setStatsError(err instanceof Error ? err.message : 'Failed to load engagement stats');
    } finally {
      setStatsLoading(false);
    }
  }, [scope, selectedSchoolId]);

  useEffect(() => {
    void loadStats();
  }, [loadStats]);

  const sortedSchools = useMemo(() => {
    const rows = [...bySchool];
    rows.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      let cmp: number;
      if (typeof av === 'string' && typeof bv === 'string') cmp = av.localeCompare(bv);
      else cmp = (av as number) - (bv as number);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [bySchool, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'schoolName' ? 'asc' : 'desc');
    }
  };

  const isAggregate = !selectedSchoolId;
  const scopeLabel = stats?.scope === 'school' ? stats.schoolName : scope === 'all' ? 'All students (platform-wide)' : 'School-affiliated students';

  const SortHeader = ({ label, k, alignRight }: { label: string; k: SortKey; alignRight?: boolean }) => (
    <th
      className={`px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none ${alignRight ? 'text-right' : 'text-left'}`}
      onClick={() => toggleSort(k)}
    >
      <span className={`inline-flex items-center gap-1 ${alignRight ? 'flex-row-reverse' : ''}`}>
        {label}
        {sortKey === k ? sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" /> : null}
      </span>
    </th>
  );

  return (
    <div className="min-h-screen bg-gray-50 font-Manrope">
      <Sidebar />
      <main className="ml-[230px] p-8">
        <div className="max-w-6xl mx-auto">
          <header className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900">Engagement</h1>
            <p className="text-gray-500 mt-1">
              How much students actually use the product, across all schools. Computed live from the database on each request.
            </p>
          </header>

          {/* Controls: school picker + scope toggle */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <select
              value={selectedSchoolId}
              onChange={(e) => setSelectedSchoolId(e.target.value)}
              className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All schools (aggregate)</option>
              {bySchool.map((s) => (
                <option key={s.schoolId} value={s.schoolId}>
                  {s.schoolName}
                </option>
              ))}
            </select>

            {isAggregate ? (
              <div className="inline-flex rounded-lg border border-gray-200 bg-white overflow-hidden text-sm">
                {(['affiliated', 'all'] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setScope(s)}
                    className={`px-3 py-2 transition-colors ${scope === s ? 'bg-primary text-white' : 'text-gray-600 hover:bg-blue-50'}`}
                  >
                    {s === 'affiliated' ? 'School-affiliated' : 'All students'}
                  </button>
                ))}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setSelectedSchoolId('')}
                className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-600 hover:bg-blue-50"
              >
                ← Back to aggregate
              </button>
            )}
            <span className="text-sm text-gray-400">Showing: {scopeLabel}</span>
          </div>

          {statsLoading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          ) : statsError ? (
            <div className="bg-red-50 border border-red-200 rounded-xl p-5 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-red-900">Couldn't load engagement stats</p>
                <p className="text-sm text-red-700 mt-1">{statsError}</p>
              </div>
            </div>
          ) : stats ? (
            <>
              <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <MetricCard
                  label="Students"
                  value={stats.totalStudents.toLocaleString()}
                  hint={`${stats.activeStudents.toLocaleString()} active · ${stats.inactiveStudents.toLocaleString()} inactive`}
                  icon={Users}
                />
                <MetricCard
                  label="Total achievements"
                  value={stats.totalAchievements.toLocaleString()}
                  hint={`${stats.studentsWithAchievements.toLocaleString()} students (${pct(stats.studentsWithAchievements, stats.totalStudents)}) logged ≥1`}
                  icon={Award}
                />
                <MetricCard
                  label="Verified achievements"
                  value={stats.verifiedAchievements.toLocaleString()}
                  hint="Distinct achievements with a VERIFIED hype request"
                  icon={BadgeCheck}
                  tone="good"
                />
                <MetricCard label="Hypes received" value={stats.hypesReceived.toLocaleString()} icon={ThumbsUp} tone="good" />
                <MetricCard
                  label="Students with a Hypepage"
                  value={stats.studentsWithHypepage.toLocaleString()}
                  hint={pct(stats.studentsWithHypepage, stats.totalStudents)}
                  icon={Layout}
                />
                <MetricCard
                  label="Students with a resume"
                  value={stats.studentsWithResume.toLocaleString()}
                  hint={pct(stats.studentsWithResume, stats.totalStudents)}
                  icon={FileText}
                />
                <MetricCard
                  label="Invites"
                  value={`${stats.acceptedInvites.toLocaleString()} accepted`}
                  hint={`${stats.pendingInvites.toLocaleString()} pending`}
                  icon={MailCheck}
                />
              </section>

              {/* Welcome / tour card — what's left of the old onboarding funnel */}
              <section className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm mb-8">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Welcome &amp; tour</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    { label: 'Saw welcome screen', value: stats.welcomeShown },
                    { label: 'Completed product tour', value: stats.tourCompleted },
                    { label: 'Onboarding completed', value: stats.onboardingCompleted },
                  ].map((row) => (
                    <div key={row.label} className="text-center">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{row.label}</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">{row.value.toLocaleString()}</p>
                      <p className="text-xs text-gray-400 mt-1">{pct(row.value, stats.totalStudents)} of students</p>
                    </div>
                  ))}
                </div>
              </section>
            </>
          ) : null}

          {/* Per-school comparison table */}
          <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="p-6 pb-3">
              <h2 className="text-lg font-bold text-gray-900">School comparison</h2>
              <p className="text-sm text-gray-500 mt-1">
                Every school side by side — the per-school breakdown behind the totals above. Sort by any column, or click a row to scope those stats to one school.
              </p>
            </div>
            {bySchoolError ? (
              <div className="px-6 pb-6 text-sm text-red-700">{bySchoolError}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-y border-gray-100">
                    <tr>
                      <SortHeader label="School" k="schoolName" />
                      <SortHeader label="Students" k="totalStudents" alignRight />
                      <SortHeader label="Active" k="activeStudents" alignRight />
                      <SortHeader label="Achievements" k="totalAchievements" alignRight />
                      <SortHeader label="Verified" k="verifiedAchievements" alignRight />
                      <SortHeader label="Hypes" k="hypesReceived" alignRight />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedSchools.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-400">
                          No schools yet
                        </td>
                      </tr>
                    ) : (
                      sortedSchools.map((s) => (
                        <tr
                          key={s.schoolId}
                          onClick={() => setSelectedSchoolId(s.schoolId)}
                          className={`border-b border-gray-50 cursor-pointer hover:bg-blue-50/50 ${selectedSchoolId === s.schoolId ? 'bg-blue-50' : ''}`}
                        >
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{s.schoolName}</td>
                          <td className="px-4 py-3 text-sm text-gray-700 text-right">{s.totalStudents.toLocaleString()}</td>
                          <td className="px-4 py-3 text-sm text-gray-700 text-right">{s.activeStudents.toLocaleString()}</td>
                          <td className="px-4 py-3 text-sm text-gray-700 text-right">{s.totalAchievements.toLocaleString()}</td>
                          <td className="px-4 py-3 text-sm text-green-700 text-right">{s.verifiedAchievements.toLocaleString()}</td>
                          <td className="px-4 py-3 text-sm text-gray-700 text-right">{s.hypesReceived.toLocaleString()}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <p className="text-xs text-gray-400 mt-6 text-center">Refresh to re-fetch. Data is computed live from the database on each request.</p>
        </div>
      </main>
    </div>
  );
}
